import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ax from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import SchedulingPage from './SchedulingPage';
import ScheduleAdminPage from './ScheduleAdminPage';
import ProfilePage from './ProfilePage';
import { setViewingAccount } from './scheduleClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Master admin only. The backend needs an account named for every schedule request, so the master admin
// opens straight onto a schedule (their own account if they have one, otherwise the first) and can switch
// from this small bar. The last account viewed is remembered across refreshes.
const LAST_KEY = 'bighat_schedule_account';
const labelOf = (a) => a.name || a.company_name || a.owner_email || a.account_id;

function AccountBar({ accounts, current, onSwitch }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-[#8892b0]" data-testid="schedule-viewing-banner">
      <label htmlFor="schedule-account-select">Viewing account</label>
      <select id="schedule-account-select" data-testid="schedule-account-select" value={current?.account_id || ''}
        onChange={(e) => onSwitch(accounts.find(a => a.account_id === e.target.value))}
        className="px-3 py-1.5 rounded-lg bg-[#141b50] border border-[rgba(251,221,104,0.2)] text-white">
        {accounts.map(a => <option key={a.account_id} value={a.account_id}>{labelOf(a)}</option>)}
      </select>
    </div>
  );
}

// One tab that switches between the three schedule views.
export default function ScheduleTab() {
  const { user } = useAuth();
  // The page lives in the URL so Back, refresh and bookmarks all work:
  //   /dashboard/schedule  /dashboard/schedule/admin  /dashboard/schedule/profile
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const view = pathname.endsWith('/admin') ? 'admin' : pathname.endsWith('/profile') ? 'profile' : 'schedule';
  const setView = (v) => navigate(v === 'schedule' ? '/dashboard/schedule' : `/dashboard/schedule/${v}`);
  const [accounts, setAccounts] = useState([]);
  const [picked, setPicked] = useState(null);
  const isMaster = user?.role === 'master_admin';
  const [loadingAccounts, setLoadingAccounts] = useState(isMaster);
  useEffect(() => { if (!isMaster) setViewingAccount(null); }, [isMaster]);

  useEffect(() => {
    if (!isMaster) return;
    setLoadingAccounts(true);
    ax.get(`${API}/admin/accounts?limit=100`)
      .then(r => {
        const list = r.data.accounts || [];
        setAccounts(list);
        let saved = null;
        try { saved = localStorage.getItem(LAST_KEY); } catch (e) { /* storage blocked */ }
        const first = list.find(a => a.account_id === saved)
          || list.find(a => a.owner_email === user?.email)
          || list[0] || null;
        setViewingAccount(first ? first.account_id : null);
        setPicked(first);
      })
      .catch(() => setAccounts([]))
      .finally(() => setLoadingAccounts(false));
  }, [isMaster, user?.email]);

  const switchAccount = (a) => {
    if (!a) return;
    try { localStorage.setItem(LAST_KEY, a.account_id); } catch (e) { /* storage blocked */ }
    setViewingAccount(a.account_id);
    setPicked(a);
  };

  if (isMaster && loadingAccounts) return <p className="text-sm text-[#8892b0]">Loading schedule...</p>;
  if (isMaster && !picked) return <p className="text-sm text-[#8892b0]" data-testid="schedule-no-accounts">No customer accounts yet, so there is no schedule to show.</p>;
  return (
    <div>
      {isMaster && <AccountBar accounts={accounts} current={picked} onSwitch={switchAccount} />}
      {view === 'admin' ? <ScheduleAdminPage key={picked?.account_id} go={setView} />
        : view === 'profile' ? <ProfilePage key={picked?.account_id} go={setView} />
        : <SchedulingPage key={picked?.account_id} go={setView} />}
    </div>
  );
}
